// "Read this page in your language" — a dialog offered once, on arrival.
//
// The site has had a full language selector all along (CustomLanguageSelector in
// the navbar: 109 languages, search, persistence, RTL). Its problem is
// discoverability, not capability — it renders as a two-letter "EN" chip that
// nobody reads as "this whole site is available in Hindi". An inline bar was
// tried first and had the same weakness: it sits in the page and gets scrolled
// past. A dialog is the version that actually gets answered.
//
// Nine cards and nothing else. There WAS a "More languages" button opening a
// searchable list of all 109, and it earned its removal twice over: it was a
// second interaction path to go wrong inside a dialog whose whole job is one
// tap, and the navbar selector already does exactly that job for anyone whose
// language is not on the grid. A prompt should ask one question.
//
// Because it interrupts, it has to earn that once and then never again:
//   - never shown if a language is already active
//   - never shown again after any answer, including "Continue in English"

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconX, IconWorld } from "@tabler/icons-react";
import { applySiteLanguage, activeSiteLanguage } from "../utils/siteLanguage";
import { isPrerender } from "../utils/isPrerender";
import styles from "./LanguagePrompt.module.css";

const ANSWERED_KEY = "auraLangPromptAnswered";

// Shown in their own script: someone who reads only Bengali should not have to
// find "Bengali" written in English.
const QUICK_PICKS: { code: string; label: string; english: string }[] = [
  { code: "hi", label: "हिन्दी", english: "Hindi" },
  { code: "bn", label: "বাংলা", english: "Bengali" },
  { code: "te", label: "తెలుగు", english: "Telugu" },
  { code: "mr", label: "मराठी", english: "Marathi" },
  { code: "ta", label: "தமிழ்", english: "Tamil" },
  { code: "gu", label: "ગુજરાતી", english: "Gujarati" },
  { code: "kn", label: "ಕನ್ನಡ", english: "Kannada" },
  { code: "ml", label: "മലയാളം", english: "Malayalam" },
];

interface Props {
  /** Overrides the default line where a page wants to be more specific. */
  message?: string;
}

export default function LanguagePrompt({
  message = "You can read this page, and your result, in your own language.",
}: Props) {
  const [open, setOpen] = useState(false);
  const cancelWidget = useRef<(() => void) | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // SELF-HEAL a snapshot that already carries the dialog.
  //
  // This ran without a prerender guard for one deploy, so react-snap captured
  // an open dialog into the static HTML for /kundli-matching — portalled to
  // <body>, therefore a SIBLING of #root. React mounts inside #root, never
  // learns that element exists, never removes it and never binds a handler to
  // it: a full-screen dead overlay that locks the page, close button included.
  // The snapshot also froze `style="overflow:hidden"` onto <body>.
  //
  // The guard below stops it being baked in again, but bad HTML is already
  // deployed and CDN-cached, so this clears whatever is on the page NOW. It
  // runs before `open` can be true, so any overlay it finds is by definition a
  // prerendered leftover and never one of ours.
  useEffect(() => {
    try {
      document
        .querySelectorAll('[class*="LanguagePrompt_overlay"]')
        .forEach((el) => el.remove());
      if (document.body.style.overflow === "hidden") {
        document.body.style.overflow = "";
      }
    } catch {
      /* nothing sensible to do; the guard below still prevents a recurrence */
    }
  }, []);

  useEffect(() => {
    // Never open during the prerender crawl. Whatever is on screen when
    // react-snap snapshots becomes the static HTML every visitor is served.
    if (isPrerender()) return;

    let answered = false;
    try {
      answered = localStorage.getItem(ANSWERED_KEY) === "1";
    } catch {
      /* storage blocked: they get asked, which is the safe direction */
    }
    if (answered) return;

    // Google Translate mounts asynchronously and the cookie is not always
    // readable on the first tick, so decide a beat later rather than opening a
    // dialog in front of someone already reading in Hindi.
    const t = setTimeout(() => {
      if (activeSiteLanguage() === null) setOpen(true);
    }, 700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => () => cancelWidget.current?.(), []);

  const close = useCallback(() => {
    setOpen(false);
    try {
      localStorage.setItem(ANSWERED_KEY, "1");
    } catch {
      /* it will ask once more next visit; better than throwing */
    }
  }, []);

  const choose = (code: string) => {
    cancelWidget.current?.();
    cancelWidget.current = applySiteLanguage(code);
    close();
  };

  // Escape closes, and focus moves into the dialog so a keyboard user is not
  // left tabbing the page behind it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    dialogRef.current?.focus();

    // Restore to "" rather than to whatever was read here. Reading the current
    // value and putting it back looks safer but is not: if this effect ever
    // runs twice, the second read sees the "hidden" the first one set and
    // restores THAT, leaving the page scroll-locked with no dialog on screen.
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close]);

  if (!open) return null;

  // Portalled to <body>: this page sits inside transformed, overflow-clipped
  // containers, and a transformed ancestor becomes the containing block for
  // position:fixed — the dialog would be trapped and clipped inside the
  // calculator card instead of covering the viewport.
  return createPortal(
    // notranslate: this asks which language you want, so it has to stay readable
    // in the language you have not chosen yet.
    <div
      className={`${styles.overlay} notranslate`}
      translate="no"
      onClick={close}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lang-dialog-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          onClick={close}
          aria-label="Close"
        >
          <IconX size={18} />
        </button>

        <div className={styles.icon} aria-hidden="true">
          <IconWorld size={26} />
        </div>
        <h2 id="lang-dialog-title" className={styles.title}>
          Choose your language
        </h2>
        <p className={styles.sub}>{message}</p>

        <div className={styles.grid}>
          {QUICK_PICKS.map((l) => (
            <button
              key={l.code}
              type="button"
              className={styles.pick}
              onClick={() => choose(l.code)}
            >
              <span className={styles.native}>{l.label}</span>
              <span className={styles.english}>{l.english}</span>
            </button>
          ))}
          {/* Full width across both columns: keeping English in the grid makes
              staying in English a choice like any other, rather than a link
              under the real options. */}
          <button
            type="button"
            className={`${styles.pick} ${styles.pickWide}`}
            onClick={() => choose("en")}
          >
            <span className={styles.native}>English</span>
            <span className={styles.english}>Continue in English</span>
          </button>
        </div>

        {/* Anything not on the grid is reachable from the globe control in the
            navbar, which carries the full list. */}
        <p className={styles.footnote}>
          More languages are available from the globe in the menu.
        </p>
      </div>
    </div>,
    document.body,
  );
}
